import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Calendar,
  FileText,
  User,
  FileSpreadsheet,
  Lock,
  ClockAlert,
  Timer,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import {
  fetchMusterRollData,
  exportMusterRollData,
  exportMusterPDF,
  getMonthMusterData,
  getMonthSummaryData,
  getMonthOvertimeData,
  getMonthLateData,
  updateEmployeeAttendance,
} from "@/services/attendanceService";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
pdfMake.vfs = pdfFonts;
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useLocation, useNavigate } from "react-router-dom";
import { DatePickerInput } from "@/components/ui/DatePickerInput";
import { formatDateTime, formatEnumLabel, formatTime } from "@/lib/utils";
import { buildCalendarPdfContent } from "@/utils/attendancePdf";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";
import { PaginationPageSize } from "@/components/ui/pagination";

const getDaysOfCurrentMonth = (selectedMonthValue) => {
  const today = new Date();
  const [selectedYear, selectedMonth] = selectedMonthValue
    ? selectedMonthValue.split("-").map(Number)
    : [];
  const year = selectedYear || today.getFullYear();
  const month = today.getMonth(); // 0–11
  const lastDay = new Date(
    year,
    (selectedMonth ? selectedMonth - 1 : month) + 1,
    0,
  ).getDate();
  const daysInMonth = Array.from({ length: lastDay }, (_, i) => i + 1);
  const firstDate = new Date(year, selectedMonth ? selectedMonth - 1 : month, 1)
    .toISOString()
    .split("T")[0];
  const lastDate = new Date(
    year,
    selectedMonth ? selectedMonth - 1 : month,
    lastDay,
  )
    .toISOString()
    .split("T")[0];
  return {
    daysInMonth,
    firstDate,
    lastDate,
    year,
    month: selectedMonth ? selectedMonth - 1 : month,
  };
};

const Reports = () => {
  const [initialLoading, setInitialLoading] = useState(true);
  const location = useLocation();
  // Links (e.g. the dashboard's late-arrivals row) can open a specific tab.
  const [reportType, setReportType] = useState(location.state?.tab || "daily");
  const [musterData, setMusterData] = useState([]);
  const [lateArrival, setLateArrival] = useState([]);
  const [overTimeData, setOverTimeData] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [monthlySummary, setMonthlySummary] = useState([]);
  const [companyDepartments, setCompanyDepartments] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedDateForSummary, setSelectedDateForSummary] = useState(
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(
      2,
      "0",
    )}`,
  );
  const [selectedLateMonth, setSelectedLateMonth] = useState(
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(
      2,
      "0",
    )}`,
  );
  const [selectedOvertimeMonth, setSelectedOvertimeMonth] = useState(
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(
      2,
      "0",
    )}`,
  );
  const [selectedMusterMonth, setSelectedMusterMonth] = useState(
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(
      2,
      "0",
    )}`,
  );
  const [selectedAttendanceCell, setSelectedAttendanceCell] = useState(null);
  const musterContainerRef = React.useRef(null);
  const { daysInMonth, year, month } =
    getDaysOfCurrentMonth(selectedMusterMonth);
  const [selectedMusterYear, selectedMusterMonthNumber] = selectedMusterMonth
    .split("-")
    .map(Number);
  const musterDays =
    selectedMusterYear && selectedMusterMonthNumber
      ? Array.from(
          {
            length: new Date(
              selectedMusterYear,
              selectedMusterMonthNumber,
              0,
            ).getDate(),
          },
          (_, i) => i + 1,
        )
      : daysInMonth;
  // Define the number of items per page for all tables
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pageStates, setPageStates] = useState({
    monthly: 1,
    late: 1,
    overtime: 1,
    daily: 1,
    muster: 1,
  });

  const handlePageSizeChange = (value) => {
    setItemsPerPage(Number(value));
    setPageStates({
      monthly: 1,
      late: 1,
      overtime: 1,
      daily: 1,
      muster: 1,
    });
  };
  const { hasFeature, isPaidOnline } = useSubscription();
  const navigate = useNavigate();

  const showDepartmentDesignation = hasFeature("department_mapping");
  const exportFeature = hasFeature("excel_pdf_export");

  const summaryCurrentPage = pageStates.monthly;

  const { paginatedMonthlySummary, summaryTotalPages, summaryTotalItems } =
    React.useMemo(() => {
      const totalItems = monthlySummary.length;
      const totalPages = Math.ceil(totalItems / itemsPerPage);
      const startIndex = (summaryCurrentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedData = monthlySummary.slice(startIndex, endIndex);

      return {
        paginatedMonthlySummary: paginatedData,
        summaryTotalPages: totalPages,
        summaryTotalItems: totalItems,
      };
    }, [monthlySummary, summaryCurrentPage, itemsPerPage]); // CRITICAL: Dependencies

  // Assuming dailyData is the full, unpaginated array of records for the daily register.
  const dailyCurrentPage = pageStates.daily;

  const { paginatedDailyData, dailyTotalPages, dailyTotalItems } =
    React.useMemo(() => {
      const totalItems = dailyData.length;
      const totalPages = Math.ceil(totalItems / itemsPerPage);
      const startIndex = (dailyCurrentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedData = dailyData.slice(startIndex, endIndex);

      return {
        paginatedDailyData: paginatedData,
        dailyTotalPages: totalPages,
        dailyTotalItems: totalItems,
      };
    }, [dailyData, dailyCurrentPage, itemsPerPage]); // CRITICAL: Dependencies

  // Assuming lateArrival is the full, unpaginated array of records.
  const lateCurrentPage = pageStates.late;

  const { paginatedLateArrival, lateTotalPages, lateTotalItems } =
    React.useMemo(() => {
      const totalItems = lateArrival.length;
      const totalPages = Math.ceil(totalItems / itemsPerPage);
      const startIndex = (lateCurrentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedData = lateArrival.slice(startIndex, endIndex);

      return {
        paginatedLateArrival: paginatedData,
        lateTotalPages: totalPages,
        lateTotalItems: totalItems,
      };
    }, [lateArrival, lateCurrentPage, itemsPerPage]); // Dependencies: Data and Page State

  // Assuming overTimeData is the full, unpaginated array of records.
  const overtimeCurrentPage = pageStates.overtime;

  const { paginatedOverTimeData, overtimeTotalPages, overtimeTotalItems } =
    React.useMemo(() => {
      const totalItems = overTimeData.length;
      const totalPages = Math.ceil(totalItems / itemsPerPage);
      const startIndex = (overtimeCurrentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedData = overTimeData.slice(startIndex, endIndex);

      return {
        paginatedOverTimeData: paginatedData,
        overtimeTotalPages: totalPages,
        overtimeTotalItems: totalItems,
      };
    }, [overTimeData, overtimeCurrentPage, itemsPerPage]); // Dependencies: Data and Page State

  // Assuming musterData is the full, unpaginated array of records.
  const musterCurrentPage = pageStates.muster; // Assuming you added 'muster: 1' to pageStates

  const { paginatedMusterData, musterTotalPages, musterTotalItems } =
    React.useMemo(() => {
      const totalItems = musterData.length;
      const totalPages = Math.ceil(totalItems / itemsPerPage);
      const startIndex = (musterCurrentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedData = musterData.slice(startIndex, endIndex);
      console.log("paginatedData============", paginatedData);

      return {
        paginatedMusterData: paginatedData,
        musterTotalPages: totalPages,
        musterTotalItems: totalItems,
      };
    }, [musterData, musterCurrentPage, itemsPerPage]); // Dependencies: Data and Page State

  useEffect(() => {
    getMusterData(true);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const target = e.target;
      if (!target.closest("[data-attendance-popover]")) {
        setSelectedAttendanceCell((prev) => {
          if (!prev) return prev;
          return null;
        });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getMusterData = async (showLoader = false) => {
    if (showLoader) setInitialLoading(true);
    try {
      const response = await fetchMusterRollData();
      console.log(response.data);
      setOverTimeData(response.data.overTime);
      setLateArrival(response.data.lateArrivals);
      setMusterData(response.data.musterAttendance);

      setDailyData(response.data.todayAttendance);
      setCompanyDepartments(response.data.companyConfig.departments);
      setMonthlySummary(response.data.monthlySummary);
    } catch (error) {
      console.error("Failed to fetch muster data:", error);
      toast.error("Failed to load muster roll");
    } finally {
      if (showLoader) setInitialLoading(false);
    }
  };

  const goToPage = (tableKey, direction) => {
    // We use the functional update form of setPageStates to ensure we get the latest state
    setPageStates((prevPageStates) => {
      const currentPage = prevPageStates[tableKey];
      const newPage = currentPage + direction;

      // Safety check to ensure newPage is not zero or negative
      if (newPage < 1) {
        return prevPageStates;
      }

      // Return a new state object with the updated page number for the specific tableKey
      return {
        ...prevPageStates,
        [tableKey]: newPage,
      };
    });
  };

  // const handleMonthChange = async (event) => {
  //   const newMonthYear = event.target.value; // Expected format: "YYYY-MM"

  //   if (!newMonthYear) return;

  //   try {
  //     const [yearStr, monthStr] = newMonthYear.split("-");
  //     const newYear = Number(yearStr);
  //     const newMonth = Number(monthStr); // This is 1-indexed (1 to 12)

  //     // 1. Update State
  //     setSelectedYear(newYear);
  //     setSelectedMonth(newMonth);

  //     // 💡 CRITICAL FIX: Update the state variable controlling the input value
  //     setSelectedDateForSummary(newMonthYear);
  //     const secondResponse = await getMonthMusterData(newYear, newMonth)
  //     console.log(secondResponse.data);
  //     setMusterData(secondResponse.data)
  //     // 2. Trigger Data Fetch
  //     const response = await getMonthSummaryData(newYear, newMonth);

  //     console.log(response);

  //     setMonthlySummary(response);
  //   } catch (error) {
  //     console.error("Error changing month:", error);
  //   }
  // };
  const handleMonthChange = async (value, type) => {
    const newMonthYear = value; // "YYYY-MM"
    if (!newMonthYear) return;

    try {
      const [yearStr, monthStr] = newMonthYear.split("-");
      const newYear = Number(yearStr);
      const newMonth = Number(monthStr);

      // Update common state if needed
      setSelectedYear(newYear);
      setSelectedMonth(newMonth);

      // 🔹 Handle based on type
      if (type === "summary") {
        setSelectedDateForSummary(newMonthYear);
        const response = await getMonthSummaryData(newYear, newMonth);
        setMonthlySummary(response.monthlySummary);
      }

      if (type === "late") {
        setSelectedLateMonth(newMonthYear);
        const response = await getMonthLateData(newYear, newMonth);
        setLateArrival(response.lateArrivals);
      }

      if (type === "muster") {
        setSelectedMusterMonth(newMonthYear);
        setSelectedAttendanceCell(null);
        const response = await getMonthMusterData(newYear, newMonth);
        setMusterData(response.data);
      }

      if (type === "ot") {
        setSelectedOvertimeMonth(newMonthYear);
        const response = await getMonthOvertimeData(newYear, newMonth);
        setOverTimeData(response.overTime);
      }
    } catch (error) {
      console.error("Error changing month:", error);
      toast.error("Failed to load monthly data.");
    }
  };

  const StatusCell = ({ status }) => {
    const colors = {
      P: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20", // Present
      A: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20", // Absent
      L: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20", // Leave
      WO: "bg-slate-300/50 text-slate-700 dark:bg-slate-700/30 dark:text-slate-400 border-slate-400/50 dark:border-slate-700/50", // Weekly Off
      H: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20", // Holiday
      HD: "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20", // Half Day
    };

    return (
      <div
        className={`w-8 h-8 flex items-center justify-center rounded text-xs font-bold border
      ${
        colors[status] ||
        "bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-700"
      }`}
      >
        {status}
      </div>
    );
  };

  const attendancePopoverPosition = React.useMemo(() => {
    if (
      !selectedAttendanceCell?.anchorRect ||
      !selectedAttendanceCell?.anchorOffset ||
      !musterContainerRef.current ||
      typeof window === "undefined"
    ) {
      return null;
    }

    const popoverWidth = 256;
    const estimatedPopoverHeight = 260;
    const containerPadding = 16;
    const offset = 8;
    const { anchorRect, anchorOffset } = selectedAttendanceCell;
    const container = musterContainerRef.current;
    const maxLeft = container.clientWidth - popoverWidth - containerPadding;

    const left = Math.max(
      containerPadding,
      Math.min(anchorOffset.left, maxLeft),
    );

    const availableBelow =
      container.clientHeight - anchorOffset.bottom - offset;
    const availableAbove = anchorOffset.top - offset;
    const canOpenBelow = availableBelow >= estimatedPopoverHeight;
    const canOpenAbove = availableAbove >= estimatedPopoverHeight;

    let top = canOpenBelow
      ? anchorOffset.bottom + offset
      : anchorOffset.top - estimatedPopoverHeight - offset;

    if (!canOpenBelow && !canOpenAbove) {
      top = Math.max(
        containerPadding,
        Math.min(
          anchorOffset.bottom + offset,
          container.clientHeight - estimatedPopoverHeight - containerPadding,
        ),
      );
    } else if (!canOpenBelow) {
      top = Math.max(
        containerPadding,
        anchorOffset.top - estimatedPopoverHeight - offset,
      );
    }

    return {
      top,
      left,
    };
  }, [selectedAttendanceCell]);

  const AttendanceLegend = () => {
    const STATUS_META = {
      P: {
        label: "Present",
        className:
          "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20",
      },
      A: {
        label: "Absent",
        className:
          "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20",
      },
      L: {
        label: "Leave",
        className:
          "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20",
      },
      WO: {
        label: "Weekly Off",
        className:
          "bg-slate-300/50 text-slate-700 dark:bg-slate-700/30 dark:text-slate-400 border-slate-400/50 dark:border-slate-700/50",
      },
      H: {
        label: "Holiday",
        className:
          "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20",
      },
      HD: {
        label: "Half Day",
        className:
          "bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20",
      },
    };

    const FALLBACK_CLASS =
      "bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-700";

    const order = ["P", "A", "L", "WO", "H", "HD"];

    return (
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        {order.map((code) => {
          const meta = STATUS_META[code];
          return (
            <div
              key={code}
              className="flex items-center gap-2 text-muted-foreground"
            >
              <div
                className={`w-6 h-6 flex items-center justify-center rounded font-bold border text-[10px] ${
                  meta?.className || FALLBACK_CLASS
                }`}
              >
                {code}
              </div>
              <span className="text-sm">{meta?.label || code}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const FeatureGatedButton = ({
    canAccess,
    onAllowedClick,
    requiredFeature,
    label,
    icon: Icon,
    loading = false,
  }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">
            <Button
              variant="outline"
              size="sm"
              disabled={loading || (!isPaidOnline && !canAccess)}
              className="flex items-center gap-2 dark:border-gray-600 dark:text-gray-300"
              onClick={() => {
                if (loading) return;

                if (!canAccess) {
                  navigate("/subscription", {
                    state: {
                      reason: "feature_upgrade_required",
                    },
                  });
                  return;
                }

                onAllowedClick();
              }}
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
                  Processing...
                </>
              ) : (
                <>
                  <Icon className="w-4 h-4" />
                  {label}
                  {!canAccess && <Lock className="w-4 h-4" />}
                </>
              )}
            </Button>
          </span>
        </TooltipTrigger>

        {!canAccess && (
          <TooltipContent>
            <p className="font-semibold">{label} – Professional Feature</p>
            <p className="text-xs">
              Upgrade to Professional or Enterprise to use this feature
            </p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );

  const downloadHolidayFileHelper = (arrayBuffer, exportDate) => {
    const fileDate = exportDate
      ? new Date(exportDate)
      : new Date(selectedMusterYear, selectedMusterMonthNumber - 1, 1);
    const monthName = fileDate.toLocaleString("default", {
      month: "short",
    });
    const fileYear = fileDate.getFullYear();
    const blob = new Blob([arrayBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = `Muster_Roll_${monthName}-${fileYear}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    window.URL.revokeObjectURL(url);
  };

  const statusMap = {
    present: { code: "P", color: "#E7F7E7" },
    absent: { code: "A", color: "#FEECEC" },
    leave: { code: "LV", color: "#FFF5D9" },
    "week-off": { code: "WO", color: "#E8F1FF" },
    holiday: { code: "H", color: "#FFF0FA" },
    "half-day": { code: "HD", color: "#FFFADF" },
    late: { code: "L", color: "#FFE6CC" },
  };

  const normalizeMusterStatusKey = (rawStatus) => {
    const raw = (rawStatus ?? "").toString().trim().toLowerCase();

    if (raw === "on-leave" || raw === "leave") return "leave";
    if (raw === "weekoff" || raw === "weekly-off" || raw === "weeklyoff")
      return "week-off";
    if (raw === "halfday" || raw === "half_day") return "half-day";

    return raw;
  };

  const loadImageAsBase64 = (url) =>
    new Promise((resolve, reject) => {
      if (!url) return resolve(null);

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        resolve(canvas.toDataURL("image/png"));
      };
      img.onerror = reject;
      img.src = url;
    });

  const getSelectedAttendanceDate = () => {
    if (!selectedMusterMonth) return null;
    return `${selectedMusterMonth}-01`;
  };

  const handleExport = async (format) => {
    try {
      const selectedDate = getSelectedAttendanceDate();
      const [selectedYear, selectedMonth] = selectedMusterMonth
        .split("-")
        .map(Number);

      if (format === "excel") {
        const res = await exportMusterRollData(selectedYear, selectedMonth);
        downloadHolidayFileHelper(res.data, `${selectedMusterMonth}-01`);
        return;
      }

      const response = await exportMusterPDF(selectedYear, selectedMonth);
      const {
        employees,
        month: responseMonth,
        year: responseYear,
        company,
      } = response.data;
      console.log("exportMusterPDF", response.data);

      const showLogo = hasFeature("custom_branding") && company?.logo_url;
      const logoBase64 = showLogo
        ? await loadImageAsBase64(company.logo_url)
        : null;

      const exportBaseDate = selectedDate
        ? new Date(selectedDate)
        : new Date(responseYear, responseMonth - 1);
      const monthName = exportBaseDate.toLocaleString("default", {
        month: "short",
      });
      const exportYear = exportBaseDate.getFullYear();
      const exportMonth = exportBaseDate.getMonth() + 1;

      const tableBody = buildMusterTable(employees, exportMonth, exportYear);

      const docDefinition = {
        pageOrientation: "landscape",
        pageSize: "A4",

        footer: (current, total) => ({
          text: `Page ${current} of ${total}`,
          alignment: "center",
          fontSize: 7,
          margin: [0, 5, 0, 0],
        }),

        content: [
          // -------------------------------------------------
          // HEADER (LOGO + COMPANY INFO + META)
          // -------------------------------------------------
          {
            table: {
              widths: ["auto", "*", "auto"],
              body: [
                [
                  showLogo && logoBase64
                    ? {
                        image: logoBase64,
                        width: 45,
                        height: 45,
                        margin: [0, 2, 8, 2],
                      }
                    : "",

                  {
                    stack: [
                      {
                        text: company.name || "",
                        style: "companyName",
                      },
                      {
                        text: `${company.contact_email || ""} | ${company.phone || ""}`,
                        style: "companyAddress",
                        margin: [0, 2, 0, 0],
                      },
                    ],
                  },

                  {
                    text: `Generated On\n${new Date().toLocaleString()}`,
                    alignment: "right",
                    style: "metaInfo",
                  },
                ],
              ],
            },
            layout: "noBorders",
            margin: [0, 0, 0, 10],
          },

          // -------------------------------------------------
          // REPORT TITLE
          // -------------------------------------------------
          {
            columns: [
              {
                text: "MUSTER ROLL",
                fontSize: 12,
                bold: true,
              },
              {
                text: `${monthName} ${exportYear}`,
                alignment: "right",
                fontSize: 10,
                bold: true,
              },
            ],
            margin: [0, 4, 0, 8],
          },

          {
            canvas: [
              {
                type: "line",
                x1: 0,
                y1: 0,
                x2: 770,
                y2: 0,
                lineWidth: 0.5,
                lineColor: "#999",
              },
            ],
            margin: [0, 0, 0, 10],
          },

          // -------------------------------------------------
          // ATTENDANCE TABLE
          // -------------------------------------------------
          {
            table: {
              headerRows: 1,
              dontBreakRows: true,
              widths: Array(tableBody[0].length).fill("auto"),
              body: tableBody,
            },
            layout: {
              paddingLeft: () => 1,
              paddingRight: () => 1,
              paddingTop: () => 1,
              paddingBottom: () => 1,
              hLineWidth: () => 0.5,
              vLineWidth: () => 0.5,
              hLineColor: () => "#999",
              vLineColor: () => "#999",
            },
          },

          // -------------------------------------------------
          // LEGEND
          // -------------------------------------------------
          {
            text: "Abbreviated Attendance Summary (Legend)",
            style: "title",
            fontSize: 7,
            margin: [0, 10, 0, 0],
          },
          {
            columns: buildHorizontalLegend(),
            columnGap: 3,
            fontSize: 7,
            margin: [0, 8, 0, 0],
          },

          // -------------------------------------------------
          // SIGNATURES
          // -------------------------------------------------
          {
            text: "Signatures",
            style: "footerTitle",
            margin: [0, 20, 0, 5],
          },
          {
            columns: [
              {
                width: "33%",
                text: "________________________\nHR Manager",
                style: "signature",
              },
              {
                width: "33%",
                text: "________________________\nDepartment Head",
                style: "signature",
                alignment: "center",
              },
              {
                width: "33%",
                text: "________________________\nAuthorized Signatory",
                style: "signature",
                alignment: "right",
              },
            ],
            margin: [0, 0, 0, 15],
          },

          // -------------------------------------------------
          // FOOTNOTE
          // -------------------------------------------------
          {
            text: "Note: This is a system-generated document and does not require a physical signature.",
            style: "footnote",
            alignment: "center",
            margin: [0, 10, 0, 0],
          },
        ],

        styles: {
          title: {
            fontSize: 14,
            bold: true,
          },
          tableHeader: {
            fontSize: 7,
            bold: true,
            alignment: "center",
          },
          tableCell: {
            fontSize: 7,
            alignment: "center",
          },
          companyName: {
            fontSize: 13,
            bold: true,
          },
          companyAddress: {
            fontSize: 8,
            color: "#555",
          },
          metaInfo: {
            fontSize: 8,
            color: "#555",
          },
          footerTitle: {
            fontSize: 10,
            bold: true,
          },
          signature: {
            fontSize: 8,
            italics: true,
          },
          footnote: {
            fontSize: 7,
            color: "#666",
          },
        },

        defaultStyle: {
          fontSize: 7,
        },
      };

      pdfMake
        .createPdf(docDefinition)
        .download(`Muster_Roll_${monthName}-${exportYear}.pdf`);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error(getErrorMessage(error, "Failed to export report"));
      }
    }
  };

  const buildMusterTable = (employees, month, year) => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const monthName = new Date(year, month - 1).toLocaleString("default", {
      month: "short",
    });
    const today = new Date();
    const isCurrentMonthExport =
      year === today.getFullYear() && month === today.getMonth() + 1;
    const currentDay = today.getDate();

    // ===== HEADER =====
    const headerRow = [
      {
        text: "Employee ID",
        bold: true,
        alignment: "center",
        margin: [1, 1, 1, 1],
      },
      { text: "Name", bold: true, alignment: "center", margin: [1, 1, 1, 1] },
      ...(showDepartmentDesignation
        ? [
            { text: "Designation", bold: true, alignment: "center" },
            { text: "Department", bold: true, alignment: "center" },
          ]
        : []),
      ...Array.from({ length: daysInMonth }, (_, i) => ({
        text: `${i + 1} ${monthName}`,
        bold: true,
        alignment: "center",
      })),

      { text: "P", bold: true },
      { text: "A", bold: true },
      { text: "LV", bold: true },
      { text: "WO", bold: true },
      { text: "H", bold: true },
      { text: "HD", bold: true },
      { text: "L", boldT: true },
      { text: "Total", bold: true },
    ];

    const body = [headerRow];

    // ===== ROWS =====
    employees.forEach((emp) => {
      const attendanceByDay = {};
      emp.attendance.forEach((d) => {
        const day = new Date(d.date).getDate();
        attendanceByDay[day] =
          isCurrentMonthExport && day > currentDay
            ? "--"
            : normalizeMusterStatusKey(d.status);
      });

      const stats = emp.attendance.reduce((acc, item) => {
        const day = new Date(item.date).getDate();
        if (isCurrentMonthExport && day > currentDay) {
          return acc;
        }
        const normalizedStatus = normalizeMusterStatusKey(item.status);
        acc[normalizedStatus] = (acc[normalizedStatus] || 0) + 1;
        return acc;
      }, {});

      const total =
        (stats["present"] || 0) +
        (stats["absent"] || 0) +
        (stats["leave"] || 0) +
        (stats["week-off"] || 0) +
        (stats["holiday"] || 0) +
        (stats["half-day"] || 0) +
        (stats["late"] || 0);

      const row = [
        {
          text: emp.employee_id || 0,
          alignment: "center",
          margin: [1, 1, 1, 1],
        },
        {
          text: emp.name || 0,
          alignment: "center",
          margin: [1, 1, 1, 1],
        },
        ...(showDepartmentDesignation
          ? [
              {
                text: emp.designation || "",
                alignment: "center",
                margin: [1, 1, 1, 1],
              },
              {
                text: emp.department || "",
                alignment: "center",
                margin: [1, 1, 1, 1],
              },
            ]
          : []),

        // ==== Daily attendance ====
        ...Array.from({ length: daysInMonth }, (_, i) => {
          const fullStatus = attendanceByDay[i + 1] || "";
          const map = statusMap[fullStatus];

          if (!map) {
            return {
              text: "",
              alignment: "center",
              margin: [1, 1, 1, 1], // ⭐ 1px spacing
            };
          }

          return {
            text: map.code,
            fillColor: map.color,
            alignment: "center",
            margin: [1, 1, 1, 1], // ⭐ Compact spacing
          };
        }),

        // ==== Summary values ====
        {
          text: (stats["present"] || 0) + (stats["late"] || 0),
          alignment: "center",
          margin: [1, 1, 1, 1],
        },
        {
          text: stats["absent"] || 0,
          alignment: "center",
          margin: [1, 1, 1, 1],
        },
        {
          text: stats["leave"] || 0,
          alignment: "center",
          margin: [1, 1, 1, 1],
        },
        {
          text: stats["week-off"] || 0,
          alignment: "center",
          margin: [1, 1, 1, 1],
        },
        {
          text: stats["holiday"] || 0,
          alignment: "center",
          margin: [1, 1, 1, 1],
        },
        {
          text: stats["half-day"] || 0,
          alignment: "center",
          margin: [1, 1, 1, 1],
        },
        { text: stats["late"] || 0, alignment: "center", margin: [1, 1, 1, 1] },

        // ⭐ Total column (safe)
        {
          text: total.toString(),
          bold: true,
          alignment: "center",
          margin: [1, 1, 1, 1],
        },
      ];

      body.push(row);
    });

    return body;
  };

  const buildHorizontalLegend = () => {
    const legendColumns = [];

    Object.entries(statusMap).forEach(([key, entry]) => {
      legendColumns.push(
        {
          width: "auto",
          canvas: [
            {
              type: "rect",
              x: 0,
              y: 0,
              w: 10,
              h: 10,
              lineWidth: 0.7,
              lineColor: "#333",
            },
            {
              type: "rect",
              x: 1.3,
              y: 1.3,
              w: 7.4,
              h: 7.4,
              color: entry.color,
            },
          ],
          margin: [0, 0, 1, 0], // tight spacing right
        },
        {
          width: "auto",
          text: `${entry.code} (${key})`,
          margin: [2, 0, 6, 0],
          fontSize: 8,
          alignment: "center",
        },
      );
    });

    return legendColumns;
  };

  function getTimeDifference(start, end) {
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);

    const startMinutes = sh * 60 + sm;
    let endMinutes = eh * 60 + em;

    if (endMinutes < startMinutes) {
      endMinutes += 24 * 60;
    }

    return endMinutes - startMinutes;
  }

  function getTimeDiffHHMM(start, end) {
    const diff = getTimeDifference(start, end);
    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;

    return `${hours}h ${minutes}m`;
  }

  const getDisplayStatus = (rawStatus) => {
    const raw = (rawStatus ?? "").toString().trim().toLowerCase();

    if (raw === "present" || raw === "late") return "P";
    if (raw === "leave" || raw === "on-leave") return "L";
    if (raw === "absent") return "A";
    if (
      raw === "week-off" ||
      raw === "weekoff" ||
      raw === "weekly-off" ||
      raw === "weeklyoff"
    )
      return "WO";
    if (raw === "holiday") return "H";
    if (raw === "halfday" || raw === "half-day" || raw === "half_day")
      return "HD";

    return raw.length > 0 ? raw[0].toUpperCase() : "--";
  };

  const handleAttendanceToggle = async () => {
    if (!selectedAttendanceCell) return;

    const current = selectedAttendanceCell.status.toLowerCase();
    const nextStatus =
      current === "present" || current === "late" ? "absent" : "present";
    const [year, month] = selectedMusterMonth.split("-").map(Number);
    try {
      console.log("Update attendance:", {
        employeeId: selectedAttendanceCell.employeeId,
        dayNumber: selectedAttendanceCell.dayNumber,
        month: month,
        year: year,
        nextStatus,
      });
      // Example API call
      await updateEmployeeAttendance({
        employeeId: selectedAttendanceCell.employeeId,
        day: selectedAttendanceCell.dayNumber,
        month,
        year,
        status: nextStatus,
      });

      setSelectedAttendanceCell(null);
      const response = await getMonthMusterData(year, month);
      setMusterData(response.data);

      // optional: refetch muster data here
      // await fetchMusterRoll();
    } catch (error) {
      console.error("Failed to update attendance", error);
    }
  };

  const downloadCalenderPdf = async (record) => {
    try {
      const [year, month] = selectedDateForSummary.split("-").map(Number);

      const response = await getMonthMusterData(year, month);

      const calenderData =
        (response?.data || []).filter(
          (calender) => calender.employee_id === record.employee_id,
        ) || [];

      if (!calenderData.length) {
        console.warn("No attendance data found");
        return;
      }

      const docDefinition = {
        pageSize: "A4",
        pageOrientation: "portrait",
        pageMargins: [20, 20, 20, 20],

        content: [
          ...buildCalendarPdfContent({
            selectedMonth: selectedDateForSummary,
            calenderData,
          }),
        ],

        styles: {
          title: {
            fontSize: 18,
            bold: true,
            color: "#0F172A",
            alignment: "center",
          },

          calendarTitle: {
            fontSize: 15,
            bold: true,
            color: "#1E293B",
          },

          calendarMonthTitle: {
            fontSize: 15,
            bold: true,
            color: "#0F172A",
            alignment: "center",
          },

          calendarHeaderCell: {
            fontSize: 8,
            bold: true,
            color: "#5F7185",
          },

          employeeLabel: {
            fontSize: 7,
            color: "#7A8A99",
          },

          employeeValue: {
            fontSize: 10,
            bold: true,
            color: "#1E293B",
            margin: [0, 2, 0, 0],
          },

          employeeValueAccent: {
            fontSize: 10,
            bold: true,
            color: "#3194A0",
            margin: [0, 2, 0, 0],
          },
        },

        defaultStyle: {
          fontSize: 9,
        },
      };

      const fileName = `attendance-${record.employee_id}-${selectedDateForSummary}.pdf`;

      pdfMake.createPdf(docDefinition).download(fileName);
    } catch (error) {
      console.error("downloadCalenderPdf error:", error);
    }
  };

  const toLocalYMD = (date) => {
    const day = new Date(date);
    const y = day.toLocaleTimeString();

    return y;
  };

  if (initialLoading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Skeleton className="h-10 w-96" />
        <Card>
          <CardContent className="p-6 space-y-3">
            <Skeleton className="h-10 w-80" />
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Attendance Reports
          </h1>
          <p className="text-muted-foreground mt-1">
            Generate and export attendance reports
          </p>
        </div>
      </div>

      {/* Report Types */}
      <Tabs value={reportType} onValueChange={setReportType} className="w-full">
        <TabsList className="flex justify-start space-x-8 overflow-x-auto bg-transparent h-auto p-0 no-scrollbar">
            <TabsTrigger value="daily">Daily Register</TabsTrigger>
            <TabsTrigger value="monthly">Monthly Summary</TabsTrigger>
            <TabsTrigger value="late">Late Report</TabsTrigger>
            <TabsTrigger value="overtime">Overtime</TabsTrigger>
            <TabsTrigger value="muster">Muster Roll</TabsTrigger>
        </TabsList>

        {/* Daily Register */}
        <TabsContent value="daily" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Daily Attendance Register
              </CardTitle>
              <CardDescription>
                Detailed daily attendance records
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                        Emp ID
                      </th>

                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                        Name
                      </th>

                      {showDepartmentDesignation && (
                        <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                          Department
                        </th>
                      )}

                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                        Shift
                      </th>

                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                        In Time
                      </th>

                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                        Out Time
                      </th>

                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                        Work Hours
                      </th>

                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                        OT Hours
                      </th>

                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Check if there is data to paginate */}
                    {paginatedDailyData && paginatedDailyData.length > 0 ? (
                      paginatedDailyData.map((record, index) => (
                        <tr
                          key={index} // Note: Using array index as key is acceptable here since the order is stable
                          className="border-b border-border hover:bg-muted/50 transition-colors"
                        >
                          {/* Employee ID */}
                          <td className="py-3 px-4 text-sm text-foreground">
                            {record.employee_id}
                          </td>
                          {/* Name */}
                          <td className="py-3 px-4 text-sm font-medium text-foreground">
                            {record.name}
                          </td>
                          {/* Department */}
                          {showDepartmentDesignation && (
                            <td className="py-3 px-4 text-sm text-muted-foreground">
                              {record.department}
                            </td>
                          )}
                          {/* Shift */}
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {record.todayShift?.name || "--"}
                          </td>
                          {/* In Time */}
                          <td className="py-3 px-4 text-sm text-foreground">
                            <div className="flex flex-col">
                              <span className="text-xs text-muted-foreground">
                                {record?.todayPunchIn
                                  ? formatTime(record.todayPunchIn)
                                  : record.todayShift?.start_time
                                    ? `Expected: ${record.todayShift?.start_time} `
                                    : "--"}
                              </span>
                            </div>
                          </td>

                          {/* Out Time */}
                          <td className="py-3 px-4 text-sm text-foreground">
                            <div className="flex flex-col">
                              <span className="text-xs text-muted-foreground">
                                {record?.todayPunchOut
                                  ? new Date(record.todayPunchOut).getHours()
                                  : record.todayShift?.end_time
                                    ? `Expected: ${record.todayShift?.end_time} `
                                    : "--"}
                              </span>
                            </div>
                          </td>
                          {/* Work Hours */}
                          <td className="py-3 px-4 text-sm text-foreground">
                            {record.todayShift?.start_time
                              ? getTimeDiffHHMM(
                                  record.todayShift.start_time,
                                  record.todayShift.end_time,
                                )
                              : "--"}
                          </td>
                          {/* OT Hours */}
                          <td className="py-3 px-4 text-sm text-primary font-medium">
                            {record.otHours || "--"}
                          </td>
                          {/* Status */}
                          <td className="py-3 px-4 text-sm text-primary">
                            {formatEnumLabel(record.todayStatus)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={showDepartmentDesignation ? 9 : 8}
                          className="py-6 text-center text-muted-foreground"
                        >
                          No daily attendance records found for this page.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls for Daily Data */}
              {dailyTotalItems > itemsPerPage && (
                <div className="flex items-center justify-end sm:justify-between gap-3 mt-4">
                  <PaginationPageSize
                    value={itemsPerPage}
                    onValueChange={handlePageSizeChange}
                  />
                  <div className="flex justify-between items-center gap-3 flex-1">
                    <p className="text-sm text-muted-foreground">
                      Page {dailyCurrentPage} of {dailyTotalPages} (Total:{" "}
                      {dailyTotalItems})
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => goToPage("daily", -1)} // Use 'daily' key
                        disabled={dailyCurrentPage === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => goToPage("daily", 1)} // Use 'daily' key
                        disabled={dailyCurrentPage === dailyTotalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Monthly Summary */}
        <TabsContent value="monthly" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                {/* LEFT SIDE: Title and Icon */}
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  <span>Monthly Attendance Summary</span>
                </div>

                {/* RIGHT SIDE: Calendar Input/Picker */}
                {/* 💡 This is where you insert your date selection component */}
                <div className="flex items-center">
                  {/* Placeholder for your actual calendar input component */}
                  <span className="text-sm text-muted-foreground mr-2 hidden sm:inline">
                    Viewing:
                  </span>
                  <DatePickerInput
                    mode="month"
                    value={selectedDateForSummary}
                    onChange={(e) => handleMonthChange(e, "summary")}
                    className="w-[130px] h-9 border-border bg-background"
                  />
                </div>
              </CardTitle>
              <CardDescription>
                Consolidated monthly attendance data
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                        Emp ID
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                        Name
                      </th>
                      <th className="text-center py-3 px-4 font-medium text-sm text-muted-foreground">
                        Total Days
                      </th>
                      <th className="text-center py-3 px-4 font-medium text-sm text-muted-foreground">
                        Present
                      </th>
                      <th className="text-center py-3 px-4 font-medium text-sm text-muted-foreground">
                        Absent
                      </th>
                      <th className="text-center py-3 px-4 font-medium text-sm text-muted-foreground">
                        Leave
                      </th>
                      <th className="text-center py-3 px-4 font-medium text-sm text-muted-foreground">
                        Late Count
                      </th>
                      <th className="text-center py-3 px-4 font-medium text-sm text-muted-foreground">
                        OT Hours
                      </th>
                      <th className="text-center py-3 px-4 font-medium text-sm text-muted-foreground">
                        Reports
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedMonthlySummary &&
                    paginatedMonthlySummary.length > 0 ? (
                      paginatedMonthlySummary.map((record, index) => (
                        <tr
                          key={index}
                          className="border-b border-border hover:bg-muted/50 transition-colors"
                        >
                          {/* 1. Employee ID */}
                          <td className="py-3 px-4 text-sm text-foreground">
                            {record.employee_id}
                          </td>
                          {/* 2. Name */}
                          <td className="py-3 px-4 text-sm font-medium text-primary">
                            {record.name}
                          </td>
                          {/* 3. Total Days */}
                          <td className="py-3 px-4 text-sm text-center text-foreground">
                            {record.totalDays}
                          </td>
                          {/* 4. Present Days */}
                          <td className="py-3 px-4 text-sm text-center text-primary font-medium">
                            {record.presentDays}
                          </td>
                          {/* 5. Absent Days */}
                          <td className="py-3 px-4 text-sm text-center text-red-600 dark:text-red-400">
                            {record.absentDays}
                          </td>
                          {/* 6. Leave Days */}
                          <td className="py-3 px-4 text-sm text-center text-blue-600 dark:text-blue-400">
                            {record.leaveDays}
                          </td>
                          {/* 7. Late Counts */}
                          <td className="py-3 px-4 text-sm text-center text-orange-600 dark:text-orange-400">
                            {record.lateCounts}
                          </td>
                          {/* 8. OT Hours */}
                          <td className="py-3 px-4 text-sm text-center text-primary font-medium">
                            {record.otHours.toFixed(2)}h
                          </td>
                          <td className="py-3 px-4 text-sm text-center text-primary font-medium">
                            <Button
                              variant="outline"
                              onClick={() => downloadCalenderPdf(record)}
                              className="dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-800"
                            >
                              Download
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={8}
                          className="py-6 text-center text-muted-foreground"
                        >
                          No monthly summary data available for this page.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {summaryTotalItems > itemsPerPage && (
                <div className="flex items-center justify-end sm:justify-between gap-3 mt-4">
                  <PaginationPageSize
                    value={itemsPerPage}
                    onValueChange={handlePageSizeChange}
                  />
                  <div className="flex justify-between items-center gap-3 flex-1">
                    <p className="text-sm text-muted-foreground">
                      Page {summaryCurrentPage} of {summaryTotalPages} (Total:
                      {summaryTotalItems})
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        // Use the generic goToPage helper
                        onClick={() => goToPage("monthly", -1)}
                        disabled={summaryCurrentPage === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        // Use the generic goToPage helper
                        onClick={() => goToPage("monthly", 1)}
                        disabled={summaryCurrentPage === summaryTotalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Other Report Types - Placeholder */}
        <TabsContent value="late" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                {/* LEFT SIDE */}
                <div className="flex items-center gap-2">
                  <ClockAlert className="h-5 w-5 text-primary" />
                  <span>Monthly Late Arrivals</span>
                </div>

                {/* RIGHT SIDE */}
                <div className="flex items-center">
                  <span className="text-sm text-muted-foreground mr-2 hidden sm:inline">
                    Viewing:
                  </span>

                  <DatePickerInput
                    mode="month"
                    value={selectedLateMonth}
                    onChange={(e) => handleMonthChange(e, "late")}
                    className="w-[130px] h-9 border-border bg-background"
                  />
                </div>
              </CardTitle>
              <CardDescription>
                Consolidated monthly late arrivals
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                          Employee ID
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                          Date
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                          Expected In
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                          Actual In
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                          Late (mins)
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                          Recorded By
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                          Reason
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {/* 💡 Use the paginatedLateArrival data */}
                      {paginatedLateArrival &&
                      paginatedLateArrival.length > 0 ? (
                        paginatedLateArrival.map((item) => (
                          <tr
                            key={item.id}
                            className="border-b border-border hover:bg-muted/50 transition-colors"
                          >
                            <td className="py-3 px-4 text-sm text-foreground">
                              <div className="flex items-center gap-4 min-w-[200px]">
                                {/* Avatar (User component assumed) */}
                                <div className="h-10 w-10 rounded-full bg-muted border border-border flex items-center justify-center text-muted-foreground">
                                  <User size={18} />
                                </div>

                                {/* Employee Info */}
                                <div>
                                  <h4 className="font-semibold text-primary text-sm leading-tight">
                                    {item.name}
                                  </h4>
                                  <p className="text-xs text-foreground">
                                    {item.employee_id}
                                  </p>
                                </div>
                              </div>
                            </td>

                            {/* Date */}
                            <td className="py-3 px-4 text-sm font-medium text-foreground">
                              {new Date(item.date).toLocaleDateString("en-IN", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })}
                            </td>

                            <td className="py-3 px-4 text-sm font-medium text-foreground">
                              {item.expected_check_in}
                            </td>
                            <td className="py-3 px-4 text-sm font-medium text-foreground">
                              {item.actual_check_in}
                            </td>

                            {/* Late minutes highlight */}
                            <td className="px-4 py-3 font-semibold text-red-600">
                              {item.late_minutes}
                            </td>

                            <td className="py-3 px-4 text-sm font-medium text-foreground">
                              {item.recorded_by}
                            </td>

                            <td className="px-4 py-3 text-sm text-muted-foreground">
                              {item.reason || "—"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={7}
                            className="text-center py-6 text-muted-foreground"
                          >
                            {/* Check if the full, unfiltered array is empty, or if pagination filtered it */}
                            {lateArrival.length === 0
                              ? "No late arrivals recorded."
                              : "No late arrivals on this page."}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* 💡 Pagination Controls for Late Arrivals */}
                {lateTotalItems > itemsPerPage && (
                  <div className="flex items-center justify-end sm:justify-between gap-3 mt-4 px-6">
                    <PaginationPageSize
                      value={itemsPerPage}
                      onValueChange={handlePageSizeChange}
                    />
                    <div className="flex justify-between items-center gap-3 flex-1">
                      <p className="text-sm text-muted-foreground">
                        Page {lateCurrentPage} of {lateTotalPages} (Total:{" "}
                        {lateTotalItems})
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => goToPage("late", -1)} // Use 'late' key
                          disabled={lateCurrentPage === 1}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => goToPage("late", 1)} // Use 'late' key
                          disabled={lateCurrentPage === lateTotalPages}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="overtime" className="space-y-4 mt-6">
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                {/* LEFT SIDE */}
                <div className="flex items-center gap-2">
                  <Timer className="h-5 w-5 text-primary" />
                  <span>Monthly Overtime Logs</span>
                </div>

                {/* RIGHT SIDE */}
                <div className="flex items-center">
                  <span className="text-sm text-muted-foreground mr-2 hidden sm:inline">
                    Viewing:
                  </span>

                  <DatePickerInput
                    mode="month"
                    value={selectedOvertimeMonth}
                    onChange={(e) => handleMonthChange(e, "ot")}
                    className="w-[130px] h-9 border-border bg-background"
                  />
                </div>
              </CardTitle>
              <CardDescription>
                Consolidated monthly overtime logs
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                        Employee
                      </th>
                      {showDepartmentDesignation && (
                        <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                          Department
                        </th>
                      )}
                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                        Date
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                        Project
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                        OT Hours
                      </th>
                      <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                        Status
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {/* 💡 Use the paginatedOverTimeData for rendering */}
                    {paginatedOverTimeData &&
                    paginatedOverTimeData.length > 0 ? (
                      paginatedOverTimeData.map((request) => (
                        <tr
                          key={request.id}
                          className="border-b border-border hover:bg-muted/50 transition-colors"
                        >
                          {/* EMPLOYEE */}
                          <td className="py-3 px-4 text-sm text-foreground">
                            <div className="flex items-center gap-3">
                              <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center border">
                                <User
                                  size={16}
                                  className="text-muted-foreground"
                                />
                              </div>
                              <div>
                                <div className="font-medium text-primary">
                                  {request.employee_name}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {request.employee_code}
                                </div>
                              </div>
                            </div>
                          </td>

                          {showDepartmentDesignation && (
                            <td className="px-4 py-3 truncate max-w-[200px]">
                              {request.department || "—"}
                            </td>
                          )}

                          {/* DATE */}
                          <td className="py-3 px-4 text-sm text-foreground">
                            {new Date(request.date).toLocaleDateString(
                              "en-IN",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              },
                            )}
                          </td>

                          {/* PROJECT */}
                          <td className="px-4 py-3 truncate max-w-[200px]">
                            {request.project || "—"}
                          </td>

                          {/* HOURS */}
                          <td className="py-3 px-4 text-sm text-foreground">
                            <span className="font-semibold text-foreground">
                              {request.ot_hours}h
                            </span>

                            {request.regular_hours > 0 && (
                              <span className="text-xs text-muted-foreground ml-1">
                                ({request.regular_hours}h reg)
                              </span>
                            )}
                          </td>

                          {/* STATUS */}
                          <td className="py-3 px-4 text-sm text-foreground">
                            <Badge
                              className={`border ${
                                request.status === "approved"
                                  ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20"
                                  : request.status === "rejected"
                                    ? "bg-red-500/10 text-red-500 border-red-500/20"
                                    : "bg-amber-500/10 text-amber-500 border-amber-500/20 hover:bg-transparent"
                              } py-1`}
                            >
                              {request.status === "approved"
                                ? "Approved"
                                : request.status === "rejected"
                                  ? "Rejected"
                                  : "Pending Review"}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={showDepartmentDesignation ? 6 : 5}
                          className="py-6 text-center text-muted-foreground"
                        >
                          {/* Check if the full, unfiltered array is empty, or if pagination filtered it */}
                          {overTimeData.length === 0
                            ? "No overtime records available."
                            : "No overtime records on this page."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* 💡 Pagination Controls */}
              {overtimeTotalItems > itemsPerPage && (
                <div className="flex items-center justify-end sm:justify-between gap-3 mt-4 px-6">
                  <PaginationPageSize
                    value={itemsPerPage}
                    onValueChange={handlePageSizeChange}
                  />
                  <div className="flex justify-between items-center gap-3 flex-1">
                    <p className="text-sm text-muted-foreground">
                      Page {overtimeCurrentPage} of {overtimeTotalPages} (Total:{" "}
                      {overtimeTotalItems})
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => goToPage("overtime", -1)} // Use 'overtime' key
                        disabled={overtimeCurrentPage === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => goToPage("overtime", 1)} // Use 'overtime' key
                        disabled={overtimeCurrentPage === overtimeTotalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="muster" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              {/* TOP ROW */}
              <div className="flex items-center justify-between gap-4">
                {/* LEFT */}
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  <CardTitle>Monthly Muster Data</CardTitle>
                </div>

                {/* RIGHT */}
                <div className="flex items-center gap-3 flex-wrap justify-end">
                  {/* Date Picker */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground hidden sm:inline">
                      Viewing:
                    </span>
                    <DatePickerInput
                      mode="month"
                      value={selectedMusterMonth}
                      onChange={(e) => handleMonthChange(e, "muster")}
                      className="w-[130px] h-9 border-border bg-background"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <FeatureGatedButton
                      canAccess={exportFeature}
                      requiredFeature="excel_pdf_export"
                      label="Export PDF"
                      icon={FileText}
                      onAllowedClick={() => handleExport("pdf")}
                    />

                    <FeatureGatedButton
                      canAccess={exportFeature}
                      requiredFeature="excel_pdf_export"
                      label="Export Excel"
                      icon={FileSpreadsheet}
                      onAllowedClick={() => handleExport("excel")}
                    />
                  </div>
                </div>
              </div>

              {/* DESCRIPTION */}
              <CardDescription>
                Consolidated monthly muster attendance
              </CardDescription>
            </CardHeader>

            <CardContent className="p-2 text-center">
              <div
                ref={musterContainerRef}
                className="relative w-full rounded-xl border border-slate-300 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden"
              >
                <div className="overflow-x-auto custom-scrollbar pb-2">
                  <table className="w-full text-left text-sm border-collapse">
                    {/* HEADER */}
                    <thead className="bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-400 uppercase text-[10px] font-bold tracking-wider sticky top-0 z-20 shadow-sm">
                      <tr>
                        {/* Employee Column */}
                        <th className="p-4 min-w-[200px] sticky left-0 z-20 bg-slate-100 dark:bg-slate-900 border-r border-slate-300 dark:border-slate-800">
                          Employee
                        </th>

                        {/* Day Columns */}
                        {musterDays.map((day) => {
                          const dateObj = new Date(year, month, day); // month-1 because JS months = 0–11
                          const weekday = dateObj.getDay(); // 0=Sun ... 6=Sat

                          return (
                            <th
                              key={day}
                              className="p-1 min-w-[40px] text-center border-r border-slate-200 dark:border-slate-800"
                            >
                              <div className="flex flex-col items-center gap-1">
                                <span className="text-slate-700 dark:text-slate-300">
                                  {day}
                                </span>
                                <span className="text-[9px] text-slate-500 dark:text-slate-500 font-normal">
                                  {
                                    [
                                      "Sun",
                                      "Mon",
                                      "Tue",
                                      "Wed",
                                      "Thu",
                                      "Fri",
                                      "Sat",
                                    ][weekday]
                                  }
                                </span>
                              </div>
                            </th>
                          );
                        })}

                        {/* Summary Columns */}
                        <th className="p-2 min-w-[50px] text-center bg-slate-100 dark:bg-slate-900 border-l border-slate-300 dark:border-slate-800 text-emerald-600 dark:text-emerald-400">
                          P
                        </th>
                        <th className="p-2 min-w-[50px] text-center bg-slate-100 dark:bg-slate-900 text-orange-600 dark:text-orange-400">
                          LT
                        </th>
                        <th className="p-2 min-w-[50px] text-center bg-slate-100 dark:bg-slate-900 text-red-600 dark:text-red-400">
                          A
                        </th>
                        <th className="p-2 min-w-[50px] text-center bg-slate-100 dark:bg-slate-900 text-amber-600 dark:text-amber-400">
                          L
                        </th>
                        <th className="p-2 min-w-[50px] text-center bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-400">
                          WO
                        </th>
                        <th className="p-2 min-w-[100px] text-center bg-slate-100 dark:bg-slate-900 text-slate-700 dark:text-slate-200">
                          Total Day's
                        </th>
                      </tr>
                    </thead>

                    {/* BODY */}
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                      {/* 💡 Use the paginatedMusterData */}
                      {paginatedMusterData && paginatedMusterData.length > 0 ? (
                        paginatedMusterData.map((emp) => {
                          // 1. --- ATTENDANCE LOOKUP MAP CREATION ---
                          const attendanceMap = new Map();

                          if (emp.attendance && emp.attendance.length > 0) {
                            emp.attendance.forEach((dayRecord) => {
                              // Get the 1-based day number from the date object
                              // Use getDate() if the UI displays local date, or getUTCDate() if it displays UTC date.
                              const dayNumber = new Date(
                                dayRecord.date,
                              ).getDate();
                              attendanceMap.set(dayNumber, dayRecord);
                            });
                          }

                          return (
                            <tr
                              key={emp.employee_id}
                              className="hover:bg-slate-100 dark:hover:bg-slate-800/40 transition-colors group"
                            >
                              {/* Employee Info */}
                              <td className="p-3 sticky left-0 z-10 bg-white dark:bg-slate-950 group-hover:bg-slate-50 dark:group-hover:bg-slate-900 border-r border-slate-200 dark:border-slate-800">
                                <div className="flex flex-col">
                                  <span className="font-medium text-slate-900 dark:text-slate-200">
                                    {emp.name}
                                  </span>
                                  {showDepartmentDesignation && (
                                    <span className="text-xs text-slate-500 dark:text-slate-400">
                                      {emp.designation}
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* Attendance cells */}
                              {musterDays.map((dayValue, idx) => {
                                const dayNumber = idx + 1;
                                const dayData = attendanceMap.get(dayNumber);

                                const rawStatus = (dayData?.status ?? "")
                                  .toString()
                                  .trim();
                                const displayStatus = dayData
                                  ? getDisplayStatus(dayData.status)
                                  : "--";

                                let cellClassName =
                                  "p-1 text-center border-r border-slate-200 dark:border-slate-800 cursor-pointer";

                                if (!dayData) {
                                  cellClassName += " text-muted-foreground";
                                }

                                return (
                                  <td
                                    key={idx}
                                    className={cellClassName}
                                    onClick={(e) => {
                                      const rect =
                                        e.currentTarget.getBoundingClientRect();
                                      const containerRect =
                                        musterContainerRef.current?.getBoundingClientRect();

                                      setSelectedAttendanceCell({
                                        employeeId: emp.employee_id,
                                        employeeName: emp.name,
                                        date: dayData?.date
                                          ? new Date(dayData.date)
                                              .toISOString()
                                              .split("T")[0]
                                          : `${selectedMusterMonth}-${String(dayNumber).padStart(2, "0")}`,
                                        dayNumber,
                                        status: rawStatus || "absent",
                                        punchIn:
                                          dayData?.punch_in ??
                                          dayData?.punchIn ??
                                          null,
                                        punchOut:
                                          dayData?.punch_out ??
                                          dayData?.punchOut ??
                                          null,
                                        anchorRect: rect,
                                        anchorOffset: containerRect
                                          ? {
                                              top: rect.top - containerRect.top,
                                              bottom:
                                                rect.bottom - containerRect.top,
                                              left:
                                                rect.left - containerRect.left,
                                            }
                                          : null,
                                      });
                                    }}
                                  >
                                    <div className="flex justify-center hover:scale-105 transition-transform">
                                      <StatusCell status={displayStatus} />
                                    </div>
                                  </td>
                                );
                              })}

                              {/* Summary Values */}
                              <td className="p-2 text-center font-bold text-emerald-600 dark:text-emerald-400 bg-slate-50 dark:bg-slate-900/20 border-l border-slate-200 dark:border-slate-800">
                                {emp.present + (emp.late || 0) + emp.halfDay * 0.5}
                              </td>
                              <td className="p-2 text-center font-bold text-orange-600 dark:text-orange-400 bg-slate-50 dark:bg-slate-900/20 border-l border-slate-200 dark:border-slate-800">
                                {emp.late || 0}
                              </td>
                              <td className="p-2 text-center font-bold text-red-600 dark:text-red-600 bg-slate-50 dark:bg-slate-900/20 border-l border-slate-200 dark:border-slate-800">
                                {emp.absent}
                              </td>
                              <td className="p-2 text-center font-bold text-amber-600 dark:text-amber-600 bg-slate-50 dark:bg-slate-900/20 border-l border-slate-200 dark:border-slate-800">
                                {emp.leave}
                              </td>
                              <td className="p-2 text-center font-bold text-slate-700 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-l border-slate-200 dark:border-slate-800">
                                {emp.weekOff}
                              </td>
                              <td className="p-2 text-center font-bold text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-900/20 border-l border-slate-200 dark:border-slate-800">
                                {emp.present +
                                  (emp.late || 0) +
                                  emp.halfDay * 0.5 +
                                  emp.absent +
                                  emp.leave +
                                  emp.weekOff +
                                  emp.holiday}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        // No muster data fallback (unchanged)
                        <tr>
                          <td
                            colSpan={musterDays.length + 7}
                            className="py-6 text-center text-muted-foreground"
                          >
                            No muster roll data available for this page.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {selectedAttendanceCell && attendancePopoverPosition && (
                    <div
                      data-attendance-popover
                      className="absolute z-50 w-64 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-xl p-4"
                      style={{
                        top: attendancePopoverPosition.top,
                        left: attendancePopoverPosition.left,
                      }}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                            {selectedAttendanceCell.employeeName}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Day {selectedAttendanceCell.dayNumber}
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => setSelectedAttendanceCell(null)}
                          className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                        >
                          ✕
                        </button>
                      </div>

                      <div className="mt-3 space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-500 dark:text-slate-400">
                            Status
                          </span>
                          <span className="font-medium text-slate-900 dark:text-slate-100 capitalize">
                            {selectedAttendanceCell.status || "absent"}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-slate-500 dark:text-slate-400">
                            Punch In
                          </span>
                          <span className="font-medium text-slate-900 dark:text-slate-100">
                            {formatTime(selectedAttendanceCell.punchIn)}
                          </span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-slate-500 dark:text-slate-400">
                            Punch Out
                          </span>
                          <span className="font-medium text-slate-900 dark:text-slate-100">
                            {formatTime(selectedAttendanceCell.punchOut)}
                          </span>
                        </div>
                      </div>

                      <div className="mt-4 flex gap-2">
                        <button
                          type="button"
                          disabled={
                            !(
                              selectedAttendanceCell.status.toLowerCase() ===
                                "present" ||
                              selectedAttendanceCell.status.toLowerCase() ===
                                "absent" ||
                              selectedAttendanceCell.status.toLowerCase() ===
                                "late"
                            )
                          }
                          onClick={handleAttendanceToggle}
                          className="flex-1 rounded-lg bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900 px-3 py-2 text-sm font-medium hover:opacity-90 transition disabled:opacity-50
    disabled:cursor-not-allowed
    disabled:active:scale-100"
                        >
                          {selectedAttendanceCell.status.toLowerCase() ===
                            "present" ||
                          selectedAttendanceCell.status.toLowerCase() === "late"
                            ? "Mark Absent"
                            : "Mark Present"}
                        </button>

                        <button
                          type="button"
                          onClick={() => setSelectedAttendanceCell(null)}
                          className="rounded-lg border border-slate-200 dark:border-slate-800 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-900 transition"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-6 p-4 border-t border-border">
                  <div className="hidden md:flex flex-wrap justify-center gap-x-8 gap-y-4 text-sm">
                    <AttendanceLegend />
                  </div>
                </div>
                {/* 💡 Pagination Controls for Muster Roll */}
                {musterTotalItems > itemsPerPage && (
                  <div className="flex items-center justify-end sm:justify-between gap-3 mt-4 px-6">
                    <PaginationPageSize
                      value={itemsPerPage}
                      onValueChange={handlePageSizeChange}
                    />
                    <div className="flex justify-between items-center gap-3 flex-1">
                      <p className="text-sm text-muted-foreground">
                        Page {musterCurrentPage} of {musterTotalPages} (Total:{" "}
                        {musterTotalItems})
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => goToPage("muster", -1)} // Use 'muster' key
                          disabled={musterCurrentPage === 1}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => goToPage("muster", 1)} // Use 'muster' key
                          disabled={musterCurrentPage === musterTotalPages}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
